import React, { useState, useEffect, useRef } from "react";
import { Send, Paperclip, Smile, X, Image as ImageIcon, File as FileIcon, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function InputArea({ onSend, prefill, onPrefillConsumed, disabled }) {
  const [value, setValue] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [attachment, setAttachment] = useState(null); // {url, media_type, filename, previewUrl?}
  const fileRef = useRef(null);
  const previewUrlRef = useRef(null); // tracks blob URL for revocation

  useEffect(() => {
    if (prefill != null) {
      setValue(prefill);
      onPrefillConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // Revoke any existing blob preview URL to avoid memory leaks
  const revokePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  // Revoke on unmount
  useEffect(() => revokePreview, []);

  const clearAttachment = () => {
    revokePreview();
    setAttachment(null);
  };

  const send = () => {
    const v = value.trim();
    if (!v && !attachment) return;
    onSend(v, attachment);
    setValue("");
    revokePreview();
    setAttachment(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (disabled) return;
    setAttaching(true);

    // Create a local blob preview immediately (before upload) for images
    let localPreview = null;
    if (f.type.startsWith("image/")) {
      revokePreview(); // discard any previous blob
      localPreview = URL.createObjectURL(f);
      previewUrlRef.current = localPreview;
      // Show preview straight away while uploading
      setAttachment({ filename: f.name, media_type: "image", url: null, previewUrl: localPreview });
    }

    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Merge the server URL with the local preview so the thumbnail stays visible
      setAttachment({ ...data, previewUrl: localPreview });
      toast.success(`${f.name} anexado`);
    } catch {
      revokePreview();
      setAttachment(null);
      toast.error("Erro ao enviar arquivo");
    }
    setAttaching(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="border-t border-slate-800 bg-slate-900 p-3">
      {attachment && (
        <div className="mb-2 flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2" data-testid="attachment-preview">
          {attachment.media_type === "image" && attachment.previewUrl ? (
            /* Image thumbnail */
            <div className="relative shrink-0">
              <img
                src={attachment.previewUrl}
                alt={attachment.filename}
                className="w-12 h-12 object-cover rounded-lg border border-slate-600"
              />
              {attaching && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
              )}
            </div>
          ) : attachment.media_type === "image" ? (
            <ImageIcon className="w-4 h-4 text-[#25D366] shrink-0" />
          ) : (
            <FileIcon className="w-4 h-4 text-[#25D366] shrink-0" />
          )}
          <span className="text-xs text-slate-200 flex-1 truncate">{attachment.filename}</span>
          {attaching && attachment.media_type !== "image" && (
            <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin shrink-0" />
          )}
          <button
            onClick={clearAttachment}
            className="text-slate-500 hover:text-red-400 p-1 shrink-0"
            title="Remover anexo"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,audio/*,video/*,application/pdf"
          disabled={disabled}
          onChange={onFile}
          className="hidden"
          data-testid="file-input"
        />
        <button
          data-testid="attach-btn"
          onClick={() => fileRef.current?.click()}
          disabled={attaching || disabled}
          className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition disabled:opacity-50"
          title="Anexar arquivo"
        >
          {attaching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
        </button>
        <div className="flex-1 relative">
          <textarea
            data-testid="chat-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Escreva uma mensagem ou use um template..."
            rows={1}
            disabled={disabled}
            className="w-full bg-slate-800 border border-slate-700 text-sm text-slate-100 placeholder-slate-500 px-3 py-2.5 pr-10 rounded-lg resize-none focus:outline-none focus:border-[#25D366]/60 focus:bg-slate-800 transition max-h-32"
          />
          <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300">
            <Smile className="w-4 h-4" />
          </button>
        </div>
        <button
          data-testid="send-btn"
          onClick={send}
          disabled={(!value.trim() && !attachment) || disabled}
          className="w-10 h-10 shrink-0 bg-[#25D366] hover:bg-[#1EAC52] disabled:bg-slate-700 disabled:text-slate-500 text-slate-950 rounded-lg flex items-center justify-center transition-all disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
